import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';
import { useInitiativeStore } from './initiativeStore';
import { useCharacterStore } from './characterStore';
import { useMobStore } from './mobStore';
import { useEncounterStore } from './encounterStore';
import { useCampaignStore } from './campaignStore';

/**
 * Combat Store — Thin proxy to encounterStore
 *
 * Maintains backward compatibility while encounterStore is the new
 * single source of truth. All mutations delegate to encounterStore.
 * Reads still work from local state (kept in sync by encounterStore
 * pushing to initiativeStore + this store's realtime subscription).
 *
 * @deprecated Prefer useEncounterStore for new code.
 */

export interface InitiativeEntry {
  characterId: string;
  initiative: number;
  dexMod?: number; // For tiebreakers
}

interface DbCombatState {
  id: string;
  scene_id: string;
  round_number: number;
  current_turn_index: number;
  is_active: boolean;
  initiative_order: InitiativeEntry[];
}

interface CombatState {
  // State
  inCombat: boolean;
  initiativeOrder: InitiativeEntry[];
  currentTurnIndex: number;
  round: number;

  // Supabase sync
  currentSceneId: string | null;
  combatStateId: string | null;
  realtimeCleanup: (() => void) | null;
  isLoading: boolean;

  // Actions
  startCombat: () => void;
  endCombat: () => void;
  /** Explicit setter — call from wherever the active scene resolves */
  setCurrentSceneId: (sceneId: string | null) => void;
  /**
   * Resync current combat state to DB without resetting — used to recover a
   * paused battle that was never written to machina_combat_state. Creates the
   * encounter, adds participants from initiativeOrder, preserves current round.
   */
  resyncToDb: (round?: number) => Promise<void>;

  // Initiative
  setInitiative: (characterId: string, initiative: number, dexMod?: number) => void;
  clearInitiative: (characterId: string) => void;
  clearAllInitiative: () => void;

  // Turn management
  nextTurn: () => void;
  prevTurn: () => void;
  setCurrentTurn: (index: number) => void;

  // Queries
  getInitiative: (characterId: string) => number | null;
  getTurnOrder: () => string[]; // Returns characterIds in initiative order

  // Supabase sync
  loadFromSupabase: (sceneId: string) => Promise<void>;
  syncToSupabase: () => Promise<void>;
  subscribeToRealtime: (sceneId: string) => void;
  unsubscribeFromRealtime: () => void;
}

export const useCombatStore = create<CombatState>((set, get) => ({
  inCombat: false,
  initiativeOrder: [],
  currentTurnIndex: 0,
  round: 1,
  currentSceneId: null,
  combatStateId: null,

  setCurrentSceneId: (sceneId) => {
    const prev = get().currentSceneId;
    if (prev !== sceneId) {
      console.log(`[combatStore] currentSceneId: ${prev ?? 'null'} → ${sceneId ?? 'null'}`);
      set({ currentSceneId: sceneId });
    }
  },

  resyncToDb: async (roundOverride) => {
    const encStore = useEncounterStore.getState();
    const sceneId = get().currentSceneId;
    const campaignId = useCampaignStore.getState().currentCampaignId;

    if (!sceneId || !campaignId) {
      console.warn('[combatStore] resyncToDb: no sceneId or campaignId — cannot resync');
      return;
    }

    // If no encounter exists, create one and mark active (without the beginCombat reset)
    if (!encStore.activeEncounter) {
      encStore.startEncounter(sceneId, campaignId);
    }

    // Add every combatant from initiativeOrder as a participant
    const charStore = useCharacterStore.getState();
    const mobStore = useMobStore.getState();
    const order = get().initiativeOrder;
    for (const entry of order) {
      const char = charStore.getCharacter(entry.characterId);
      const mob = char ? null : mobStore.mobs.get(entry.characterId);
      if (!char && !mob) continue;
      const existing = useEncounterStore.getState().activeEncounter?.participants.get(entry.characterId);
      if (existing) {
        useEncounterStore.getState().setParticipantInitiative(entry.characterId, entry.initiative);
      } else {
        const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
        const type = char ? 'character' as const : 'mob' as const;
        useEncounterStore.getState().addParticipant(
          entry.characterId, type, name, entry.initiative, entry.dexMod ?? 0
        );
      }
    }

    // Manually flip to active status + preserve current round (avoid beginCombat's reset)
    const enc = useEncounterStore.getState().activeEncounter;
    if (enc) {
      const preservedRound = roundOverride ?? Math.max(1, get().round);
      useEncounterStore.setState({
        activeEncounter: { ...enc, status: 'active', round: preservedRound },
      });
      await useEncounterStore.getState().syncToSupabase();
      set({ inCombat: true });
      console.log(`[combatStore] resyncToDb complete — round ${preservedRound}, ${order.length} combatants`);
    }
  },
  realtimeCleanup: null,
  isLoading: false,

  startCombat: () => {
    const encStore = useEncounterStore.getState();
    const sceneId = get().currentSceneId;
    const campaignId = useCampaignStore.getState().currentCampaignId;
    const charStore = useCharacterStore.getState();
    const mobStore = useMobStore.getState();

    // Self-heal: create encounter if one doesn't exist yet
    if (!encStore.activeEncounter && sceneId && campaignId) {
      encStore.startEncounter(sceneId, campaignId);
    }

    // Seed encounter from existing initiative order (rolls the player may
    // already have made), then auto-include ALL alive mobs on the board that
    // aren't in init yet. No more 'Add Monsters' management step — if a mob
    // is on the board and combat starts, it's in the fight.
    if (useEncounterStore.getState().activeEncounter) {
      const encParticipants = useEncounterStore.getState().activeEncounter!.participants;

      // 1) Existing initiativeOrder entries
      for (const entry of get().initiativeOrder) {
        if (encParticipants.has(entry.characterId)) continue;
        const char = charStore.getCharacter(entry.characterId);
        const mob = char ? null : mobStore.mobs.get(entry.characterId);
        if (!char && !mob) continue;
        const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
        const type = char ? 'character' as const : 'mob' as const;
        useEncounterStore.getState().addParticipant(
          entry.characterId, type, name, entry.initiative, entry.dexMod ?? 0
        );
      }

      // 2) Alive mobs that don't already have an init roll — auto-roll now
      const refreshed = useEncounterStore.getState().activeEncounter!.participants;
      for (const mob of mobStore.mobs.values()) {
        if (mob.dead) continue;
        if (refreshed.has(mob.id)) continue;
        const dexMod = Math.floor((mob.dexterity - 10) / 2);
        const initiative = Math.floor(Math.random() * 20) + 1 + dexMod;
        useEncounterStore.getState().addParticipant(
          mob.id, 'mob', mob.displayName || mob.name, initiative, dexMod
        );
        // Keep combatStore's initiativeOrder in sync for UI reads
        set((state) => ({
          initiativeOrder: [
            ...state.initiativeOrder.filter((e) => e.characterId !== mob.id),
            { characterId: mob.id, initiative, dexMod },
          ].sort((a, b) => b.initiative - a.initiative || (b.dexMod || 0) - (a.dexMod || 0)),
        }));
      }
    }

    const withEncounter = !!useEncounterStore.getState().activeEncounter;
    if (withEncounter) {
      useEncounterStore.getState().beginCombat();
    }
    set({ inCombat: true, round: 1, currentTurnIndex: 0 });
    if (!withEncounter) get().syncToSupabase();
  },

  endCombat: () => {
    const encStore = useEncounterStore.getState();
    const hadEncounter = !!encStore.activeEncounter;
    if (hadEncounter) {
      encStore.endEncounter(); // encounterStore handles DB sync + clears initiativeStore
    } else {
      // Clear initiative panel when no encounter was active
      useInitiativeStore.getState().clearAllEntries();
    }
    set({
      inCombat: false,
      initiativeOrder: [],
      currentTurnIndex: 0,
      round: 1
    });
    if (!hadEncounter) get().syncToSupabase();
  },

  setInitiative: (characterId, initiative, dexMod = 0) => {
    // Bridge to encounterStore — auto-create encounter if needed
    const encStore = useEncounterStore.getState();
    if (!encStore.activeEncounter) {
      const sceneId = get().currentSceneId;
      const campaignId = useCampaignStore.getState().currentCampaignId;
      if (sceneId && campaignId) {
        encStore.startEncounter(sceneId, campaignId);
      }
    }
    const delegated = !!encStore.activeEncounter;
    if (delegated) {
      const existing = encStore.activeEncounter!.participants.get(characterId);
      if (existing) {
        encStore.setParticipantInitiative(characterId, initiative);
      } else {
        // Resolve name and type
        const charStore = useCharacterStore.getState();
        const mobStore = useMobStore.getState();
        const char = charStore.getCharacter(characterId);
        const mob = char ? null : mobStore.mobs.get(characterId);
        const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
        const type = char ? 'character' as const : 'mob' as const;
        encStore.addParticipant(characterId, type, name, initiative, dexMod);
      }
    }

    set((state) => {
      const filtered = state.initiativeOrder.filter(e => e.characterId !== characterId);
      const newOrder = [...filtered, { characterId, initiative, dexMod }];
      newOrder.sort((a, b) => {
        if (b.initiative !== a.initiative) return b.initiative - a.initiative;
        return (b.dexMod || 0) - (a.dexMod || 0);
      });
      return {
        initiativeOrder: newOrder,
        inCombat: state.inCombat || newOrder.length > 0,
      };
    });

    // When not delegated to encounterStore, push to initiativeStore directly
    // so mob entries appear in the initiative panel UI
    if (!delegated) {
      const initStore = useInitiativeStore.getState();
      const charStore = useCharacterStore.getState();
      const mobStore = useMobStore.getState();
      const char = charStore.getCharacter(characterId);
      const mob = char ? null : mobStore.mobs.get(characterId);
      const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
      const type = char ? 'character' as const : 'mob' as const;

      // Use char-${id} prefix for characters to match InitiativePanel + encounterStore convention
      const panelId = char ? `char-${characterId}` : characterId;
      const existing = initStore.entries.find(e => e.id === panelId);
      if (existing) {
        initStore.setInitiative(panelId, initiative);
      } else {
        initStore.addEntry({
          id: panelId,
          name,
          initiative,
          dexMod,
          type,
          characterId: char ? characterId : undefined,
          conditions: [],
        });
      }

      get().syncToSupabase();
    }
  },

  clearInitiative: (characterId) => {
    const encStore = useEncounterStore.getState();
    const delegated = !!encStore.activeEncounter;
    if (delegated) {
      encStore.removeParticipant(characterId);
    }
    set((state) => ({
      initiativeOrder: state.initiativeOrder.filter(e => e.characterId !== characterId),
    }));
    if (!delegated) get().syncToSupabase();
  },

  clearAllInitiative: () => {
    set({ initiativeOrder: [] });
    useInitiativeStore.getState().clearAllEntries();
    get().syncToSupabase();
  },

  nextTurn: () => {
    const encStore = useEncounterStore.getState();
    const delegated = !!encStore.activeEncounter;
    if (delegated) {
      encStore.nextTurn(); // encounterStore handles DB sync
    }
    set((state) => {
      if (state.initiativeOrder.length === 0) return state;
      const nextIndex = (state.currentTurnIndex + 1) % state.initiativeOrder.length;
      const newRound = nextIndex === 0 ? state.round + 1 : state.round;
      return { currentTurnIndex: nextIndex, round: newRound };
    });
    if (!delegated) get().syncToSupabase();
  },

  prevTurn: () => {
    const encStore = useEncounterStore.getState();
    const delegated = !!encStore.activeEncounter;
    if (delegated) {
      encStore.prevTurn(); // encounterStore handles DB sync
    }
    set((state) => {
      if (state.initiativeOrder.length === 0) return state;
      const prevIndex = state.currentTurnIndex === 0
        ? state.initiativeOrder.length - 1
        : state.currentTurnIndex - 1;
      const newRound = state.currentTurnIndex === 0 && state.round > 1
        ? state.round - 1
        : state.round;
      return { currentTurnIndex: prevIndex, round: newRound };
    });
    if (!delegated) get().syncToSupabase();
  },

  setCurrentTurn: (index) => {
    set({ currentTurnIndex: index });
    get().syncToSupabase();
  },

  getInitiative: (characterId) => {
    const entry = get().initiativeOrder.find(e => e.characterId === characterId);
    return entry?.initiative ?? null;
  },

  getTurnOrder: () => get().initiativeOrder.map(e => e.characterId),

  // ==========================================================================
  // SUPABASE SYNC
  // ==========================================================================

  loadFromSupabase: async (sceneId) => {
    set({ isLoading: true, currentSceneId: sceneId });
    try {
      const { data, error } = await supabase
        .from('machina_combat_state')
        .select('*')
        .eq('scene_id', sceneId)
        .single();

      if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows

      if (data) {
        const dbState = data as DbCombatState;

        // Skip inactive encounters — prevents ghost mobs from stale records
        if (!dbState.is_active) {
          console.log('[CombatStore] Skipping inactive encounter (stale combat_state)');
          set({ inCombat: false, initiativeOrder: [], currentTurnIndex: 0, round: 1, combatStateId: null, isLoading: false });
          return;
        }

        const entries = dbState.initiative_order || [];
        set({
          inCombat: dbState.is_active,
          initiativeOrder: entries,
          currentTurnIndex: dbState.current_turn_index,
          round: dbState.round_number,
          combatStateId: dbState.id,
          isLoading: false,
        });

        // Bridge: populate initiative panel store from combat state
        const initStore = useInitiativeStore.getState();
        const charStore = useCharacterStore.getState();
        const mobStore = useMobStore.getState();
        const validPanelIds = new Set<string>();

        for (const entry of entries) {
          const char = charStore.getCharacter(entry.characterId);
          const mob = char ? null : mobStore.mobs.get(entry.characterId);
          // Skip unresolvable entries (stale mobs from previous sessions)
          if (!char && !mob) continue;
          const panelId = char ? `char-${entry.characterId}` : entry.characterId;
          validPanelIds.add(panelId);
          const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
          const type = char ? 'character' : 'mob';

          const existing = initStore.entries.find(e => e.id === panelId);
          if (!existing) {
            initStore.addEntry({
              id: panelId,
              name,
              initiative: entry.initiative,
              dexMod: entry.dexMod || 0,
              type,
              characterId: char ? entry.characterId : undefined,
              conditions: [],
            });
          } else {
            initStore.setInitiative(panelId, entry.initiative);
          }
        }

        // Remove stale mob entries that are no longer in the DB data
        for (const existing of initStore.entries) {
          if (existing.type === 'mob' && !validPanelIds.has(existing.id)) {
            initStore.removeEntry(existing.id);
          }
        }
      } else {
        set({
          inCombat: false,
          initiativeOrder: [],
          currentTurnIndex: 0,
          round: 1,
          combatStateId: null,
          isLoading: false,
        });
      }
    } catch (err) {
      console.error('Failed to load combat state:', err);
      set({ isLoading: false });
    }
  },

  syncToSupabase: async () => {
    const { currentSceneId, combatStateId, inCombat, initiativeOrder, currentTurnIndex, round } = get();
    if (!currentSceneId) return;

    try {
      const payload = {
        scene_id: currentSceneId,
        is_active: inCombat,
        initiative_order: initiativeOrder,
        current_turn_index: currentTurnIndex,
        round_number: round,
        updated_at: new Date().toISOString(),
      };

      if (combatStateId) {
        const { error } = await supabase
          .from('machina_combat_state')
          .update(payload)
          .eq('id', combatStateId);
        if (error) throw error;
      } else {
        const { data, error } = await supabase
          .from('machina_combat_state')
          .insert(payload)
          .select('id')
          .single();
        if (error) throw error;
        if (data) {
          set({ combatStateId: data.id });
        }
      }
    } catch (err) {
      console.error('Failed to sync combat state:', err);
    }
  },

  subscribeToRealtime: (sceneId) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `combat:${sceneId}`,
      table: 'machina_combat_state',
      filter: `scene_id=eq.${sceneId}`,
      event: '*',
      onPayload: (payload) => {
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const dbState = payload.new as unknown as DbCombatState;
          const entries = dbState.initiative_order || [];
          set({
            inCombat: dbState.is_active,
            initiativeOrder: entries,
            currentTurnIndex: dbState.current_turn_index,
            round: dbState.round_number,
            combatStateId: dbState.id,
          });

          // Bridge: sync initiative entries to the initiative panel store
          const initStore = useInitiativeStore.getState();
          const charStore = useCharacterStore.getState();
          const mobStore = useMobStore.getState();
          const validPanelIds = new Set<string>();

          for (const entry of entries) {
            const char = charStore.getCharacter(entry.characterId);
            const mob = char ? null : mobStore.mobs.get(entry.characterId);
            if (!char && !mob) continue;
            const panelId = char ? `char-${entry.characterId}` : entry.characterId;
            validPanelIds.add(panelId);
            const name = char?.name || mob?.displayName || mob?.name || 'Unknown';
            const type = char ? 'character' : 'mob';

            const existing = initStore.entries.find(e => e.id === panelId);
            if (existing) {
              if (existing.initiative !== entry.initiative) {
                initStore.setInitiative(panelId, entry.initiative);
              }
            } else {
              initStore.addEntry({
                id: panelId,
                name,
                initiative: entry.initiative,
                dexMod: entry.dexMod || 0,
                type,
                characterId: char ? entry.characterId : undefined,
                conditions: [],
              });
            }
          }

          // Remove stale mob entries that are no longer in the DB data
          for (const existing of initStore.entries) {
            if (existing.type === 'mob' && !validPanelIds.has(existing.id)) {
              initStore.removeEntry(existing.id);
            }
          }
        } else if (payload.eventType === 'DELETE') {
          set({
            inCombat: false,
            initiativeOrder: [],
            currentTurnIndex: 0,
            round: 1,
            combatStateId: null,
          });
        }
      },
    });

    set({ realtimeCleanup: cleanup, currentSceneId: sceneId });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));
